/**
 * @file useSpaceAnalytics.js
 * @description Composable for space data analysis and aggregation
 *
 * This composable provides analytics functions for analyzing space data,
 * particularly for aggregating airflow supply data and detecting temperature sensors.
 */

/**
 * Composable for space analytics and data aggregation
 *
 * @returns {Object} Space analytics functions
 */
export function useSpaceAnalytics() {
  /**
   * Get airflow supply data for a space
   *
   * This function aggregates airflow supply data from multiple possible sources:
   * 1. Pre-loaded API airflow data (primary source)
   * 2. Zone Sections under AHUs serving the space
   * 3. VAV/Terminal Units directly in the space
   * 4. Direct equipment connections to the space
   *
   * The function performs complex traversal of the hierarchy tree to find all
   * equipment that supplies air to the given space, including:
   * - AHUs with zone sections that serve the space
   * - VAV units placed in the space
   * - Terminal units with metadata linking to the space
   *
   * @param {Object} node - Space node from hierarchy tree
   * @param {string} node.type - Must be 'space'
   * @param {Object} node.data - Node data containing space details
   * @param {number|string} node.data.id - Space ID (numeric or "space-123" format)
   * @param {string} node.label - Display name of the space
   * @param {Array} node.children - Child nodes (equipment, devices, points)
   *
   * @param {Object} dependencies - Required data and state
   * @param {Object} dependencies.spaceAirflowData - Pre-loaded airflow data indexed by space ID
   * @param {Array} dependencies.treeNodes - Full hierarchy tree for traversal
   *
   * @returns {string|null} Formatted airflow supply chain string or null if none found
   *
   * @example
   * // Returns: "AHU B-1-1 → Zone 1, AHU B-1-1 → Zone 2"
   * const airflow = getSpaceAirflowSupply(spaceNode, {
   *   spaceAirflowData: airflowDataRef.value,
   *   treeNodes: treeNodesRef.value
   * })
   */
  const getSpaceAirflowSupply = (node, dependencies) => {
    if (node.type !== 'space') {return null;}

    const { spaceAirflowData, treeNodes } = dependencies;

    // Get the raw ID from the node
    let spaceId = node.data?.id || node.id;

    // Extract numeric ID from either format ("space-173" or 173)
    if (typeof spaceId === 'string' && spaceId.includes('space-')) {
      spaceId = parseInt(spaceId.replace('space-', ''));
    } else if (typeof spaceId === 'string') {
      spaceId = parseInt(spaceId);
    }

    // First check if we have pre-loaded airflow data from API using numeric ID
    if (spaceAirflowData && spaceAirflowData[spaceId]) {
      return spaceAirflowData[spaceId];
    }

    // Fall back to static hierarchy search
    const supplyChains = [];

    // Helper function to search through hierarchy for AHUs and zones serving this space
    const findSupplyChain = (items, parentPath = []) => {
      if (!items || !Array.isArray(items)) {return;}

      items.forEach((item) => {
        // Check if this is an AHU
        if (item.type === 'equipment' &&
            (item.data?.brick_class?.includes('Air_Handling_Unit') ||
             item.brick_class?.includes('Air_Handling_Unit') ||
             item.brickClass?.includes('Air_Handling_Unit'))) {

          const ahuName = item.label || item.data?.name || 'Unknown AHU';

          // Check its children for zone sections or VAV units
          if (item.children && Array.isArray(item.children)) {
            item.children.forEach((child) => {
              // Check for Zone Section equipment
              if (child.type === 'equipment' &&
                  (child.data?.brick_class?.includes('Zone') ||
                   child.brick_class?.includes('Zone') ||
                   child.brickClass?.includes('Zone') ||
                   child.data?.brick_class?.includes('VAV') ||
                   child.data?.brick_class?.includes('Terminal_Unit'))) {

                const zoneName = child.label || child.data?.name || 'Unknown Zone';
                const metadata = child.data?.metadata || {};

                // Check if this zone serves our space (multiple possible fields)
                // Note: spaceId is already converted to numeric at this point
                const servesSpace =
                  metadata.serves_space_id === spaceId ||
                  metadata.serves_space_ids?.includes(spaceId) ||
                  metadata.space_id === spaceId ||
                  metadata.space_ids?.includes(spaceId) ||
                  metadata.served_spaces?.includes(spaceId) ||
                  // Also check if zone name mentions the space name
                  (node.label && zoneName.toLowerCase().includes(node.label.toLowerCase()));

                if (servesSpace) {
                  supplyChains.push({
                    ahu: ahuName,
                    zone: zoneName,
                  });
                }
              }
            });
          }
        }

        // Also check for VAV/Terminal units directly in spaces
        if (item.type === 'equipment' &&
            (item.data?.brick_class?.includes('VAV') ||
             item.data?.brick_class?.includes('Terminal_Unit') ||
             item.brick_class?.includes('VAV') ||
             item.brick_class?.includes('Terminal_Unit'))) {

          // Check if this VAV is in our space (by checking parent)
          const isInOurSpace = parentPath.some((parent) => {
            if (parent.type !== 'space') {return false;}

            // Extract numeric ID from parent for comparison
            let parentSpaceId = parent.data?.id || parent.id;
            if (typeof parentSpaceId === 'string' && parentSpaceId.includes('space-')) {
              parentSpaceId = parseInt(parentSpaceId.replace('space-', ''));
            } else if (typeof parentSpaceId === 'string') {
              parentSpaceId = parseInt(parentSpaceId);
            }

            return parentSpaceId === spaceId;
          });

          if (isInOurSpace) {
            // Try to find which AHU serves this VAV
            const vavName = item.label || item.data?.name || 'Unknown VAV';

            // Look for AHU in the metadata or trace up the hierarchy
            const metadata = item.data?.metadata || {};
            if (metadata.served_by_ahu || metadata.ahu_name) {
              supplyChains.push({
                ahu: metadata.served_by_ahu || metadata.ahu_name,
                zone: vavName,
              });
            } else {
              // If no explicit AHU link, just note the VAV exists
              supplyChains.push({
                ahu: 'System',
                zone: vavName,
              });
            }
          }
        }

        // Recurse into children
        if (item.children) {
          findSupplyChain(item.children, [...parentPath, item]);
        }
      });
    };

    // Search the entire hierarchy (only if treeNodes is available)
    try {
      if (treeNodes && Array.isArray(treeNodes)) {
        findSupplyChain(treeNodes);
      }
    } catch (error) {
      console.error('Failed to search hierarchy for airflow supply chain:', error);
    }

    // Also check if space has VAV/Terminal units as direct children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child) => {
        if (child.type === 'equipment' &&
            (child.data?.brick_class?.includes('VAV') ||
             child.data?.brick_class?.includes('Terminal_Unit'))) {
          const vavName = child.label || child.data?.name || 'Unknown VAV';

          // Check if we already found this in supply chains
          const alreadyFound = supplyChains.some((chain) => chain.zone === vavName);
          if (!alreadyFound) {
            supplyChains.push({
              ahu: 'HVAC System',
              zone: vavName,
            });
          }
        }
      });
    }

    // Format the supply chains for display
    if (supplyChains.length > 0) {
      // Remove duplicates and format
      const uniqueChains = Array.from(new Set(
        supplyChains.map((s) => `${s.ahu} → ${s.zone}`),
      ));
      return uniqueChains.join(', ');
    }

    return null;
  };

  /**
   * Get temperature sensors for a space
   *
   * This function searches the entire hierarchy for temperature-related sensors
   * that are associated with the given space. It checks multiple node types:
   * - Equipment (thermostats, temp sensors)
   * - Devices (temp sensors)
   * - Points (temperature measurement points)
   * - Zones serving the space (and their sensors)
   *
   * Association is determined by:
   * 1. Metadata fields (space_id, serves_space_id, monitored_space_id, etc.)
   * 2. Physical location (location_space_id)
   * 3. Parent path (is the sensor a child of this space?)
   * 4. Name matching (does sensor name include space name?)
   *
   * @param {Object} node - Space node from hierarchy tree
   * @param {string} node.type - Must be 'space'
   * @param {Object} node.data - Node data containing space details
   * @param {number|string} node.data.id - Space ID (numeric or "space-123" format)
   * @param {string} node.label - Display name of the space
   * @param {Array} node.children - Child nodes
   *
   * @param {Object} dependencies - Required data and state
   * @param {Array} dependencies.treeNodes - Full hierarchy tree for traversal
   *
   * @returns {Array<Object>} Array of temperature sensor objects
   *
   * @example
   * // Returns: [
   * //   { name: "Zone Temp Sensor", location: "AHU B-1-1 > Zone 1", id: "device-456" },
   * //   { name: "Room Thermostat", location: "Local", id: "equipment-789" }
   * // ]
   * const sensors = getSpaceTemperatureSensors(spaceNode, {
   *   treeNodes: treeNodesRef.value
   * })
   */
  const getSpaceTemperatureSensors = (node, dependencies) => {
    if (node.type !== 'space') {return [];}

    const { treeNodes } = dependencies;

    const sensors = [];
    // Get the raw ID from the node
    let spaceId = node.data?.id || node.id;

    // Extract numeric ID from either format ("space-173" or 173)
    if (typeof spaceId === 'string' && spaceId.includes('space-')) {
      spaceId = parseInt(spaceId.replace('space-', ''));
    } else if (typeof spaceId === 'string') {
      spaceId = parseInt(spaceId);
    }

    const spaceName = node.label || node.data?.name;

    // Helper function to find all temperature-related sensors
    const findTemperatureSensors = (items, parentPath = []) => {
      if (!items || !Array.isArray(items)) {return;}

      items.forEach((item) => {
        // Build location string from parent path
        const itemLocation = parentPath.length > 0 ?
          parentPath.map((p) => p.label || p.data?.name).filter(Boolean).join(' > ') :
          'System';

        // Check if this is a temperature sensor or thermostat (as equipment)
        if (item.type === 'equipment' &&
            (item.data?.brick_class?.includes('Temperature') ||
             item.data?.brick_class?.includes('Thermostat') ||
             item.data?.brick_class?.includes('Temp_Sensor') ||
             item.brick_class?.includes('Temperature') ||
             item.brick_class?.includes('Thermostat') ||
             item.brickClass?.includes('Temperature') ||
             item.brickClass?.includes('Thermostat'))) {

          const sensorName = item.label || item.data?.name || 'Unknown Sensor';
          const metadata = item.data?.metadata || {};

          // Check if this sensor is associated with our space
          const isForOurSpace =
            // Direct space association
            metadata.space_id === spaceId ||
            metadata.serves_space_id === spaceId ||
            metadata.monitored_space_id === spaceId ||
            metadata.space_ids?.includes(spaceId) ||
            // Check if sensor is physically in our space
            metadata.location_space_id === spaceId ||
            item.data?.location_space_id === spaceId ||
            // Check if parent is our space
            parentPath.some((parent) => {
              if (parent.type !== 'space') {return false;}

              // Extract numeric ID from parent for comparison
              let parentSpaceId = parent.data?.id || parent.id;
              if (typeof parentSpaceId === 'string' && parentSpaceId.includes('space-')) {
                parentSpaceId = parseInt(parentSpaceId.replace('space-', ''));
              } else if (typeof parentSpaceId === 'string') {
                parentSpaceId = parseInt(parentSpaceId);
              }

              return parentSpaceId === spaceId;
            }) ||
            // Check if sensor name references our space name
            (spaceName && sensorName.toLowerCase().includes(spaceName.toLowerCase()));

          if (isForOurSpace) {
            sensors.push({
              name: sensorName,
              location: itemLocation,
              id: item.id || item.data?.id,
            });
          }
        }

        // Check if this is a device type sensor
        if (item.type === 'device' &&
            (item.data?.brick_class?.includes('Temperature') ||
             item.data?.brick_class?.includes('Thermostat') ||
             item.data?.brick_class?.includes('Temp_Sensor') ||
             item.brick_class?.includes('Temperature') ||
             item.brick_class?.includes('Thermostat') ||
             item.brickClass?.includes('Temperature') ||
             item.brickClass?.includes('Thermostat'))) {

          const sensorName = item.label || item.data?.name || 'Unknown Sensor';
          const metadata = item.data?.metadata || {};

          // Check if this sensor is associated with our space
          const isForOurSpace =
            // Direct space association
            metadata.space_id === spaceId ||
            metadata.serves_space_id === spaceId ||
            metadata.monitored_space_id === spaceId ||
            metadata.space_ids?.includes(spaceId) ||
            // Check if sensor is physically in our space
            metadata.location_space_id === spaceId ||
            item.data?.location_space_id === spaceId ||
            // Check if parent is our space
            parentPath.some((parent) => {
              if (parent.type !== 'space') {return false;}

              // Extract numeric ID from parent for comparison
              let parentSpaceId = parent.data?.id || parent.id;
              if (typeof parentSpaceId === 'string' && parentSpaceId.includes('space-')) {
                parentSpaceId = parseInt(parentSpaceId.replace('space-', ''));
              } else if (typeof parentSpaceId === 'string') {
                parentSpaceId = parseInt(parentSpaceId);
              }

              return parentSpaceId === spaceId;
            }) ||
            // Check if sensor name references our space name
            (spaceName && sensorName.toLowerCase().includes(spaceName.toLowerCase()));

          if (isForOurSpace) {
            sensors.push({
              name: sensorName,
              location: itemLocation,
              id: item.id || item.data?.id,
            });
          }
        }

        // Check if this is a point type sensor
        if (item.type === 'point' &&
            (item.data?.brick_class?.includes('Temperature') ||
             item.data?.brick_class?.includes('Thermostat') ||
             item.brick_class?.includes('Temperature') ||
             item.brickClass?.includes('Temperature'))) {

          const sensorName = item.label || item.data?.name || 'Unknown Sensor';
          const metadata = item.data?.metadata || {};

          // Check if this sensor is associated with our space
          const isForOurSpace =
            metadata.space_id === spaceId ||
            metadata.serves_space_id === spaceId ||
            metadata.monitored_space_id === spaceId ||
            // Check if parent path contains our space
            parentPath.some((parent) =>
              parent.type === 'space' && (parent.data?.id === spaceId || parent.id === spaceId),
            ) ||
            // Check if sensor name references our space name
            (spaceName && sensorName.toLowerCase().includes(spaceName.toLowerCase()));

          if (isForOurSpace) {
            sensors.push({
              name: sensorName,
              location: itemLocation,
              id: item.id || item.data?.id,
            });
          }
        }

        // Check if this is a zone that might serve our space
        if (item.type === 'equipment' &&
            (item.data?.brick_class?.includes('Zone') ||
             item.brick_class?.includes('Zone') ||
             item.brickClass?.includes('Zone'))) {

          const metadata = item.data?.metadata || {};
          const servesOurSpace =
            metadata.serves_space_id === spaceId ||
            metadata.serves_space_ids?.includes(spaceId) ||
            metadata.space_id === spaceId ||
            metadata.space_ids?.includes(spaceId);

          // If this zone serves our space, look for sensors in it
          if (servesOurSpace && item.children && Array.isArray(item.children)) {
            item.children.forEach((zoneChild) => {
              if ((zoneChild.type === 'device' || zoneChild.type === 'equipment' || zoneChild.type === 'point') &&
                  (zoneChild.data?.brick_class?.includes('Temperature') ||
                   zoneChild.data?.brick_class?.includes('Thermostat') ||
                   zoneChild.brick_class?.includes('Temperature') ||
                   zoneChild.brickClass?.includes('Temperature'))) {

                sensors.push({
                  name: zoneChild.label || zoneChild.data?.name,
                  location: `${item.label || 'Zone'}`,
                  id: zoneChild.id || zoneChild.data?.id,
                });
              }
            });
          }
        }

        // Recursively check children
        if (item.children && Array.isArray(item.children)) {
          findTemperatureSensors(item.children, [...parentPath, item]);
        }
      });
    };

    // Search the entire hierarchy (only if treeNodes is available)
    try {
      if (treeNodes && Array.isArray(treeNodes)) {
        findTemperatureSensors(treeNodes);
      }
    } catch (error) {
      console.error('Failed to search hierarchy for temperature sensors:', error);
    }

    // Also check if there are any sensors directly in the space itself
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child) => {
        if ((child.type === 'equipment' || child.type === 'device') &&
            (child.data?.brick_class?.includes('Temperature') ||
             child.data?.brick_class?.includes('Thermostat') ||
             child.brick_class?.includes('Temperature') ||
             child.brick_class?.includes('Thermostat') ||
             child.brickClass?.includes('Temperature') ||
             child.brickClass?.includes('Thermostat'))) {

          // Check if we already have this sensor
          const alreadyFound = sensors.some((s) => s.id === (child.id || child.data?.id));
          if (!alreadyFound) {
            sensors.push({
              name: child.label || child.data?.name,
              location: 'Local',
              id: child.id || child.data?.id,
            });
          }
        }
      });
    }

    // Remove duplicates based on id
    const uniqueSensors = [];
    const seenIds = new Set();
    sensors.forEach((sensor) => {
      if (sensor.id && !seenIds.has(sensor.id)) {
        uniqueSensors.push(sensor);
        seenIds.add(sensor.id);
      }
    });

    return uniqueSensors;
  };

  return {
    getSpaceAirflowSupply,
    getSpaceTemperatureSensors,
  };
}
